import { ENTITY_ATTRIBUTE_TYPE } from "src/consts/entityAttribute";
import {
  formatPositionLocation,
  getPositionDisplayName,
  getPositionOccupancyStatusLabel,
  type PositionOccupancyStatus,
} from "src/util/position";
import {
  formatTrimmedOverlayValue,
  type OverlayAttributeRegistry,
} from "./overlayAttributes";
import { formatDirectWorkforceDisplay } from "./personOverlayDisplay";
import {
  getOverlayAttributeDisplayValue,
  isOverlayBagKey,
  parseOverlayBagKey,
  type OverlayAttributeBadgeSource,
} from "./personOverlayAttributeKeys";

export type PersonOverlayValueSource = OverlayAttributeBadgeSource & {
  attributes?: Array<{
    id?: string | null;
    attributeType?: string;
    subtype?: string | null;
    label?: string | null;
    value?: unknown;
    deleted?: boolean;
  }> | null;
  /** Admin-only full bag; whitelisted `attributes` omits custom overlay keys. */
  allAttributes?: PersonOverlayValueSource["attributes"];
  country?: string | null;
  city?: string | null;
  state?: string | null;
  address?: string | null;
  company?: string | null;
  timezone?: string | null;
  bands?: string[] | null;
  directWorkforce?: boolean | null;
  jobTitle?: string | null;
  name?: string | null;
  preferredName?: string | null;
  firstName?: string | null;
  surname?: string | null;
  fte?: number | null;
  establishedFte?: number | null;
  occupancyStatus?: PositionOccupancyStatus | null;
  isVacancy?: boolean | null;
  aggregateId?: string | null;
  id?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

const formatBandsOverlayValue = (bands: unknown): string | null => {
  if (!Array.isArray(bands) || bands.length === 0) {
    return null;
  }
  const parts = bands.map((band) => String(band).trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

type PersonBagAttributeRow = NonNullable<
  PersonOverlayValueSource["attributes"]
>[number];

const SYSTEM_PERSON_BAG_ATTRIBUTE_TYPES = new Set<string>([
  "id",
  ...Object.values(ENTITY_ATTRIBUTE_TYPE),
]);

const isDisplayablePersonBagAttribute = (
  attr: PersonBagAttributeRow
): attr is PersonBagAttributeRow & { attributeType: string } => {
  const attributeType = attr.attributeType;
  return (
    typeof attributeType === "string" &&
    attributeType.length > 0 &&
    !SYSTEM_PERSON_BAG_ATTRIBUTE_TYPES.has(attributeType)
  );
};

const bagAttributesFromList = (
  attributes: PersonOverlayValueSource["attributes"]
) =>
  (attributes ?? []).filter(
    (row) => !row.deleted && isDisplayablePersonBagAttribute(row)
  );

export const getPersonBagAttributes = (person: PersonOverlayValueSource) => {
  const all = bagAttributesFromList(person.allAttributes);
  return all.length > 0 ? all : bagAttributesFromList(person.attributes);
};

const resolveBagOverlayDisplayValueFromPerson = (
  key: string,
  person: PersonOverlayValueSource
): string | null => {
  const parsed = parseOverlayBagKey(key);
  if (!parsed) {
    return null;
  }
  const match = getPersonBagAttributes(person).find((attr) => {
    if (attr.attributeType !== parsed.attributeType) {
      return false;
    }
    return (attr.subtype ?? "") === parsed.subtype;
  });
  return formatTrimmedOverlayValue(match?.value);
};

const SCALAR_OVERLAY_RESOLVERS = {
  country: (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.country),
  city: (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.city),
  state: (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.state),
  address: (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.address),
  company: (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.company),
  timezone: (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.timezone),
  bands: (person: PersonOverlayValueSource) =>
    formatBandsOverlayValue(person.bands),
  directWorkforce: (person: PersonOverlayValueSource) =>
    formatDirectWorkforceDisplay(person.directWorkforce),
  fte: (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.fte),
  "position.jobTitle": (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.jobTitle),
  "position.name": (person: PersonOverlayValueSource) =>
    getPositionDisplayName(person),
  "position.location": (person: PersonOverlayValueSource) =>
    formatPositionLocation(person),
  "position.fte": (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.fte ?? person.establishedFte),
  "position.status": (person: PersonOverlayValueSource) => {
    if (person.occupancyStatus) {
      return getPositionOccupancyStatusLabel(person.occupancyStatus);
    }
    if (person.isVacancy === true) {
      return getPositionOccupancyStatusLabel("VACANT");
    }
    return null;
  },
  "position.positionId": (person: PersonOverlayValueSource) =>
    formatTrimmedOverlayValue(person.aggregateId ?? person.id),
  "position.seatDates": (person: PersonOverlayValueSource) => {
    const start = formatTrimmedOverlayValue(person.startDate);
    const end = formatTrimmedOverlayValue(person.endDate);
    if (!start && !end) {
      return null;
    }
    return `${start ?? "—"} – ${end ?? "Ongoing"}`;
  },
} as const satisfies Record<
  | "country"
  | "city"
  | "state"
  | "address"
  | "company"
  | "timezone"
  | "bands"
  | "directWorkforce"
  | "fte"
  | "position.jobTitle"
  | "position.name"
  | "position.location"
  | "position.fte"
  | "position.status"
  | "position.positionId"
  | "position.seatDates",
  (person: PersonOverlayValueSource) => string | null
>;

const isScalarOverlayResolverKey = (
  key: string
): key is keyof typeof SCALAR_OVERLAY_RESOLVERS =>
  Object.prototype.hasOwnProperty.call(SCALAR_OVERLAY_RESOLVERS, key);

export const resolvePersonOverlayAttributeValue = (
  person: PersonOverlayValueSource | null | undefined,
  key: string
): string | null => {
  if (!person) {
    return null;
  }
  const fromOverlay = getOverlayAttributeDisplayValue(key, person);
  if (fromOverlay) {
    return fromOverlay;
  }
  if (isOverlayBagKey(key)) {
    return resolveBagOverlayDisplayValueFromPerson(key, person);
  }
  if (isScalarOverlayResolverKey(key)) {
    return SCALAR_OVERLAY_RESOLVERS[key](person);
  }
  return null;
};

export const personOverlayRegistry: OverlayAttributeRegistry<PersonOverlayValueSource> =
  {
    resolveValue: (person, key) =>
      resolvePersonOverlayAttributeValue(person, key),
  };
